import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FeatureGate from '../components/FeatureGate';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import supabase from '../supabase/supabase';

const { FiVideo, FiPlay, FiCheckCircle, FiClock, FiYoutube, FiExternalLink, FiEdit3, FiSave, FiAlertCircle, FiTrendingUp, FiLoader } = FiIcons;

function ContentPlanning() {
  const { user } = useAuth();
  const { getActiveUserId } = useGuardian();
  const [activeTab, setActiveTab] = useState('guidelines');
  const [youtubeChannel, setYoutubeChannel] = useState('');
  const [isEditingChannel, setIsEditingChannel] = useState(false);
  const [savedChannel, setSavedChannel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => { loadYouTubeChannel(); }, [user]);

  const loadYouTubeChannel = async () => {
    try {
      setLoading(true);
      const activeUserId = getActiveUserId();
      const { data, error } = await supabase.from('profiles').select('youtube_channel').eq('user_id', activeUserId).single();
      if (error && error.code !== 'PGRST116') throw error;
      if (data && data.youtube_channel) { setSavedChannel(data.youtube_channel); setYoutubeChannel(data.youtube_channel); }
    } catch (error) {
      console.error('Error loading YouTube channel:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChannel = async () => {
    try {
      setSaving(true);
      const activeUserId = getActiveUserId();
      const { error } = await supabase.from('profiles').update({ youtube_channel: youtubeChannel, updated_at: new Date().toISOString() }).eq('user_id', activeUserId);
      if (error) throw error;
      setSavedChannel(youtubeChannel);
      setIsEditingChannel(false);
      setMessage({ type: 'success', text: 'YouTube channel saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save YouTube channel' });
    } finally {
      setSaving(false);
    }
  };

  const contentGuidelines = [
    {
      title: 'Highlight Reel', duration: '3-5 minutes', icon: FiVideo,
      tips: ['Start with your best plays to grab attention immediately', 'Include a variety of skills and situations', 'Use high-quality footage with good lighting', 'Add game stats and context (score, opponent, date)', 'Keep transitions smooth and professional'],
    },
    {
      title: 'Skills Showcase', duration: '2-3 minutes', icon: FiTrendingUp,
      tips: ['Demonstrate fundamental skills specific to your position', 'Show both practice drills and game situations', 'Include measurables (speed, vertical jump, etc.)', 'Film from multiple angles', 'Keep clips concise and impactful'],
    },
    {
      title: 'Introduction Video', duration: '1-2 minutes', icon: FiPlay,
      tips: ['Introduce yourself and your athletic background', 'Mention your academic achievements and goals', 'Explain why you want to play at the college level', 'Be authentic and professional', 'Speak clearly and make eye contact with the camera'],
    },
  ];

  const videoChecklist = [
    'Film in landscape (horizontal) orientation',
    'Use good lighting (natural light or well-lit fields)',
    'Ensure audio is clear if narrating',
    'Edit out downtime and irrelevant footage',
    'Add text overlays with stats and context',
    'Include your name, position, and grad year in title',
    'Upload in HD quality (1080p minimum)',
    'Create eye-catching thumbnail',
  ];

  return (
    <FeatureGate feature="Content Planning">
      <div className="min-h-screen bg-gray-50 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-space-grotesk font-bold text-secondary mb-2">Content Planning Hub</h1>
            <p className="text-sm sm:text-base text-gray-600">Create compelling video content that showcases your athletic abilities to college coaches.</p>
          </div>

          {message.text && (
            <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm sm:text-base">{message.text}</span>
            </div>
          )}

          {/* YouTube Channel */}
          <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100 mb-6 sm:mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiYoutube} className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                <h2 className="text-lg sm:text-xl font-space-grotesk font-semibold text-secondary">YouTube Channel</h2>
              </div>
              {!isEditingChannel && savedChannel && (
                <button onClick={() => setIsEditingChannel(true)} className="text-primary hover:text-blue-700 transition-colors flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base">
                  <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <SafeIcon icon={FiLoader} className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : (
              <>
                {isEditingChannel || !savedChannel ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">YouTube Channel URL</label>
                      <input type="url" value={youtubeChannel} onChange={(e) => setYoutubeChannel(e.target.value)} placeholder="https://youtube.com/@username" className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:text-base" />
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      <button onClick={handleSaveChannel} disabled={saving || !youtubeChannel} className="bg-primary text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 text-sm sm:text-base">
                        <SafeIcon icon={FiSave} className="w-4 h-4" />
                        <span>{saving ? 'Saving...' : 'Save Channel'}</span>
                      </button>
                      {isEditingChannel && (
                        <button onClick={() => { setIsEditingChannel(false); setYoutubeChannel(savedChannel); }} className="px-5 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base text-center">
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50 p-4 rounded-lg gap-3">
                    <div className="flex items-center space-x-3 min-w-0">
                      <SafeIcon icon={FiYoutube} className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span className="text-gray-800 font-medium text-sm sm:text-base truncate">{savedChannel}</span>
                    </div>
                    <a href={savedChannel} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-primary hover:text-blue-700 transition-colors text-sm sm:text-base flex-shrink-0">
                      <span>View Channel</span>
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6 sm:mb-8">
            <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto">
              {[{ id: 'guidelines', label: 'Content Guidelines' }, { id: 'checklist', label: 'Video Checklist' }].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <motion.div key={activeTab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {activeTab === 'guidelines' && (
              <div className="space-y-5 sm:space-y-6">
                {contentGuidelines.map((guideline, index) => (
                  <motion.div key={index} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start space-x-3 sm:space-x-4 mb-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={guideline.icon} className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-space-grotesk font-semibold text-secondary mb-1">{guideline.title}</h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <SafeIcon icon={FiClock} className="w-4 h-4" />
                          <span>Recommended: {guideline.duration}</span>
                        </div>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {guideline.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start space-x-3 text-gray-600">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm sm:text-base">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            )}
            {activeTab === 'checklist' && (
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg sm:text-xl font-space-grotesk font-semibold text-secondary mb-5 sm:mb-6">Video Production Checklist</h3>
                <div className="space-y-3">
                  {videoChecklist.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <input type="checkbox" className="w-4 h-4 sm:w-5 sm:h-5 text-primary rounded focus:ring-2 focus:ring-primary flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-6 sm:mt-8 bg-blue-50 border-l-4 border-blue-400 p-4 sm:p-6 rounded">
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Pro Tips</h4>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Upload videos regularly to keep coaches engaged</li>
                  <li>Use descriptive titles with your name, position, and graduation year</li>
                  <li>Add your contact information in video descriptions</li>
                  <li>Create playlists to organize different types of content</li>
                  <li>Share your videos on social media and in coach emails</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FeatureGate>
  );
}

export default ContentPlanning;